<template>
  <DossierDocumentCard
    title="Certificado do Corpo de Bombeiros"
    :status-options="statusOptions"
    :multiple-status="true"
    :status-requires-validity="['possui_vigente', 'possui_provisorio']"
    :status-requires-upload="['possui_vigente', 'possui_provisorio']"
    :explainer-text="explainerText"
    :mode="mode"
    :not-applicable="false"
    :start-collapsed="startCollapsed"
    v-model="formData"
    save-endpoint="/rest-home/dossier/fire-dept"
    upload-endpoint="/rest-home/dossier/fire-dept/upload"
    @saved="onSaved"
    @error="onError"
  >
    <!-- Campos específicos: tipo do certificado, número e UF -->
    <template #fields-before>
      <div class="dossier-module__fields">
        <!-- Tipo do certificado -->
        <div
          class="dossier-module__field-group dossier-module__field-group--small"
        >
          <label class="dossier-module__label dossier-module__label--required">
            Tipo de certificado
          </label>
          <v-select
            v-if="!isViewMode"
            v-model="formData.certificate_type"
            :items="certificateTypes"
            dense
            outlined
            hide-details="auto"
            :rules="[rules.required]"
            placeholder="Selecione"
            class="dossier-module__input"
          />
          <span v-else class="dossier-module__view-value">{{
            formData.certificate_type || "—"
          }}</span>
        </div>

        <!-- Número / protocolo -->
        <div
          class="dossier-module__field-group dossier-module__field-group--small"
        >
          <label class="dossier-module__label">Número / protocolo</label>
          <v-text-field
            v-if="!isViewMode"
            v-model="formData.certificate_number"
            dense
            outlined
            hide-details
            placeholder="Número do documento"
            class="dossier-module__input"
          />
          <span v-else class="dossier-module__view-value">{{
            formData.certificate_number || "—"
          }}</span>
        </div>

        <!-- UF do Corpo de Bombeiros emissor -->
        <div
          class="dossier-module__field-group dossier-module__field-group--xsmall"
        >
          <label class="dossier-module__label">UF emissor</label>
          <v-text-field
            v-if="!isViewMode"
            v-model="formData.uf"
            dense
            outlined
            hide-details
            maxlength="2"
            placeholder="SP"
            class="dossier-module__input"
          />
          <span v-else class="dossier-module__view-value">{{
            formData.uf || "—"
          }}</span>
        </div>
      </div>
    </template>

    <!-- Nota sobre CLCB vs AVCB no rodapé -->
    <template #footer>
      <div v-if="!isViewMode" class="dossier-firedept__note">
        <i class="fas fa-info-circle mr-1"></i>
        <strong>AVCB</strong> (Auto de Vistoria) é emitido após vistoria
        presencial. <strong>CLCB</strong> (Certificado de Licença) é emitido
        para edificações de baixo risco mediante autodeclaração. A exigência
        varia por estado e porte do imóvel.
      </div>
    </template>
  </DossierDocumentCard>
</template>

<script>
import DossierDocumentCard from "../dossier/DossierDocumentCard.vue";

const EXPLAINER_TEXT = `
  O certificado do Corpo de Bombeiros atesta que a edificação atende às normas
  de segurança contra incêndio e pânico vigentes no estado.<br><br>
  Pode ser o <strong>AVCB</strong> (Auto de Vistoria do Corpo de Bombeiros),
  emitido após vistoria presencial, ou o <strong>CLCB</strong> (Certificado de
  Licença do Corpo de Bombeiros), para edificações de baixo risco.<br><br>
  A exigência de cada tipo varia por estado, município e porte da edificação.
  Consulte o Corpo de Bombeiros da sua região.<br><br>
  O Sistema Clin não valida o conteúdo do documento, apenas registra sua
  existência para fins de organização e apresentação à fiscalização.
`;

export default {
  name: "DossierFireDept",
  components: { DossierDocumentCard },
  props: {
    mode: {
      type: String,
      default: "edit",
      validator: (v) => ["edit", "view"].includes(v),
    },
    initialData: { type: Object, default: () => ({}) },
    startCollapsed: { type: Boolean, default: false },
  },
  data() {
    return {
      explainerText: EXPLAINER_TEXT,
      statusOptions: [
        { value: "possui_vigente", label: "Possui certificado vigente" },
        {
          value: "possui_provisorio",
          label: "Possui certificado provisório / protocolo",
        },
        {
          value: "em_elaboracao",
          label: "Em processo de vistoria ou renovação",
        },
        { value: "nao_possui", label: "Não possui (pendente)" },
      ],
      certificateTypes: ["AVCB", "CLCB"],
      formData: {
        certificate_type: null,
        certificate_number: "",
        uf: "",
        status: [],
        validity_date: null,
        file_url: null,
        file_name: null,
        observations: "",
        ...this.flattenInitialData(),
      },
      rules: {
        required: (v) => !!v || "Campo obrigatório",
      },
    };
  },
  computed: {
    isViewMode() {
      return this.mode === "view";
    },
  },
  watch: {
    initialData: {
      deep: true,
      handler() {
        Object.assign(this.formData, this.flattenInitialData());
      },
    },
  },
  methods: {
    flattenInitialData() {
      const d = this.initialData || {};
      return {
        certificate_type: d.certificate_type || null,
        certificate_number: d.certificate_number || "",
        uf: d.uf || "",
        status: d.status || [],
        validity_date: d.validity_date || null,
        file_url: d.file_url || null,
        file_name: d.file_name || null,
        observations: d.observations || "",
      };
    },
    onSaved(data) {
      this.$emit("saved", { module: "fire_dept", data });
    },
    onError(errors) {
      this.$emit("error", { module: "fire_dept", errors });
    },
  },
};
</script>

<style scoped>
.dossier-firedept__note {
  font-size: 12px;
  color: #555;
  background: #f8f8f7;
  border-left: 3px solid #1f5a76;
  border-radius: 0 4px 4px 0;
  padding: 8px 10px;
  margin-top: 4px;
  line-height: 1.6;
}
</style>